/**
 * React hooks for tenant data management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantService } from '@/services';
import type { Tenant, TenantCreateRequest, TenantUpdateRequest, PaginationOptions, TenantStatistics, TenantFilterOptions } from '@/types';

const TENANT_QUERY_KEYS = {
  all: ['tenants'] as const,
  lists: () => [...TENANT_QUERY_KEYS.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...TENANT_QUERY_KEYS.lists(), { filters }] as const,
  details: () => [...TENANT_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...TENANT_QUERY_KEYS.details(), id] as const,
};

/**
 * Hook to fetch tenants with pagination and filtering
 */
export function useTenants(options?: PaginationOptions, filters?: TenantFilterOptions) {
  return useQuery({
    queryKey: [...TENANT_QUERY_KEYS.lists(), options, filters],
    queryFn: () => tenantService.getTenants(options, filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a specific tenant
 */
export function useTenant(id: string, enabled = true) {
  return useQuery({
    queryKey: TENANT_QUERY_KEYS.detail(id),
    queryFn: () => tenantService.getTenant(id),
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create a new tenant
 */
export function useCreateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TenantCreateRequest) => tenantService.createTenant(data),
    onSuccess: () => {
      // Invalidate and refetch the tenants list and all statistics (filtered and unfiltered)
      queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: [...TENANT_QUERY_KEYS.all, 'statistics'] });
    },
  });
}

/**
 * Hook to update an existing tenant
 */
export function useUpdateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TenantUpdateRequest }) =>
      tenantService.updateTenant(id, data),
    onSuccess: (updatedTenant: Tenant) => {
      // Update the specific tenant in cache
      queryClient.setQueryData(
        TENANT_QUERY_KEYS.detail(updatedTenant.id),
        updatedTenant
      );
      // Invalidate the tenants list and all statistics to ensure consistency
      queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: [...TENANT_QUERY_KEYS.all, 'statistics'] });
    },
  });
}

/**
 * Hook to delete a tenant
 */
export function useDeleteTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tenantService.deleteTenant(id),
    onSuccess: (_: void, deletedId: string) => {
      // Remove the tenant from cache
      queryClient.removeQueries({ queryKey: TENANT_QUERY_KEYS.detail(deletedId) });
      // Invalidate the tenants list and all statistics
      queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: [...TENANT_QUERY_KEYS.all, 'statistics'] });
    },
  });
}

/**
 * Hook to fetch tenant statistics with optional filtering
 */
export function useTenantStatistics(filters?: TenantFilterOptions) {
  return useQuery({
    queryKey: [...TENANT_QUERY_KEYS.all, 'statistics', filters],
    queryFn: () => tenantService.getTenantStatistics(filters),
    staleTime: 1 * 60 * 1000, // 1 minute - cache statistics for shorter time to see updates faster
  });
}
